import React, { useContext } from 'react';
import { createUseStyles } from 'react-jss';
import { StaticContext } from '@sm/webassets/StaticContext';
import { t } from '@sm/intl';
import { generateMetricsAttribute } from '@sm/metrics';
import { LogoWithText, Logo } from '@sm/webassets/Logos';
import { createURL } from '@sm/utils';

import { Box } from '@wds/box';
import { Typography } from '@wds/typography';

import { CmsWrenchTheme } from '~app/styles/cmsTheme';
import { COPY } from '~app/components/SimpleLogoHeader';

export type Props = {
  utSource: string;
  utSource2: string;
  isThanksText?: boolean;
  amplitudeEvent?: string;
  buildWithAI?: boolean;
};

const THANK_COPY = {
  heading: 'Thank you for taking this survey.',
};

const useStyles = createUseStyles(({ type, breakpoints, spacing, brandColors }: CmsWrenchTheme) => ({
  mainBox: {
    width: '100%',
    height: '74px',
  },
  logo: {
    height: 24,
    [`@media (min-width: ${breakpoints.md}px)`]: {
      height: 44,
    },
  },
  buildWithAIText: {
    '& span': {
      color: brandColors.neutral.white,
      fontSize: type.fontSize.cardTitle,
      fontWeight: type.fontWeight.medium,
      [`@media (min-width: ${breakpoints.md}px)`]: {
        fontSize: type.fontSize.sectionTitle,
      },
    },
  },
  thankslogo: {
    fontSize: type.fontSize.cardTitle,
    fontWeight: type.fontWeight.medium,
    alignSelf: 'center',
    marginTop: -spacing[1],
    paddingRight: spacing[2],
    marginBottom: 0,
    [`@media (min-width: ${breakpoints.sm}px)`]: {
      fontSize: (type.fontSize.cardTitle as number) + 6,
    },
    '& svg g': {
      color: brandColors.primary.heritageGreen,
      fill: brandColors.primary.heritageGreen,
    },
  },
  customClass: {
    '& div span a': {
      color: brandColors.primary.cleanSlate,
    },
    '& g': {
      color: brandColors.primary.cleanSlate,
      fill: brandColors.primary.cleanSlate,
    },
  },
}));

const ColoredLogoWithText = ({
  utSource,
  utSource2,
  isThanksText = false,
  amplitudeEvent,
  buildWithAI = false,
}: Props): React.ReactElement => {
  const { mainBox, thankslogo, logo, buildWithAIText, customClass } = useStyles();
  const utSource3 = 'ColoredLogoWithText';
  const {
    smParams: { surveyId, collectorId, respondentId },
    templateType,
  } = useContext(StaticContext);

  const metricsData = {
    amplitudeEvent,
    itemSelected: 'logo',
    templateType,
    surveyId,
    collectorId,
    respondentId,
    log: { message: `${utSource}-${utSource2}-${utSource3}-LogoWithText` },
  };

  if (buildWithAI) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" className={`${mainBox} ${buildWithAIText}`}>
        <Typography variant="cardTitle" weight="medium" component="span">
          {THANK_COPY.heading}
        </Typography>
      </Box>
    );
  }

  return (
    <Box display="flex" justifyContent="center" alignItems="center" className={mainBox}>
      <a
        style={{ lineHeight: '0rem' }}
        aria-label={t(COPY.LOGO_ARIA_LABEL)}
        data-testid="ColoredLogoWithText__LogoWithText"
        href={createURL('/', {
          ut_source: utSource,
          ut_source2: utSource2,
          ut_source3: utSource3,
          ut_ctatext: 'LogoWithText',
        })}
        data-sm-metrics={generateMetricsAttribute({ data: metricsData })}
      >
        {isThanksText ? <Logo className={thankslogo} /> : <LogoWithText className={`${logo} ${customClass}`} />}
      </a>
      {isThanksText && (
        <Typography variant="cardTitle" weight="medium" component="span" className={logo}>
          {THANK_COPY.heading}
        </Typography>
      )}
    </Box>
  );
};

export default ColoredLogoWithText;
